import React, { useEffect, useCallback, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import { resolveAssetUrl } from '../services/apiService';
import { useLanguage } from '../context/LanguageContext';
import { i18n } from '../i18n/i18n';
import { isGlobalCatalogAdmin } from '../utils/catalogAdmin';
import { KidIcon, type KidIconName } from './kid/KidIcon';
import ConfirmDialog from './kid/ConfirmDialog';
import { avatarOrDefault } from '../utils/avatars';

// One layout on every screen (Jens, 2026-10-11): phone, tablet and computer all get the top bar
// with the menu button, the slide-out menu and the bottom tab bar, so children recognise the app
// on any device. (Previously tablets and computers ≥ 768 px got a separate top menu bar instead.)

const HOME_PATH = '/home';

// Flat icons from the kid-first redesign (src/components/kid/KidIcon.tsx). The admin area uses the
// "Grown-ups" lock (D9).
const NAV_TAB_ICONS: Record<'home' | 'tasks' | 'calendar' | 'rewards' | 'stats', KidIconName> = {
  home: 'home',
  tasks: 'tasks',
  calendar: 'calendar',
  rewards: 'rewards',
  stats: 'stats',
};

const NAV_UI_ICONS: Record<'admin' | 'settings', KidIconName> = {
  admin: 'lock',
  settings: 'settings',
};

function HamburgerIcon({ open }: { open: boolean }) {
  return (
    <svg
      className="w-6 h-6 text-app-text dark:text-gray-100"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden
    >
      {open ? (
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      ) : (
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
      )}
    </svg>
  );
}


const Header: React.FC = () => {
  const { current_user, current_family, deviceLogout, endMemberTurn, switchUser } = useAppContext() as any;
  const pendingApprovals = ((current_family?.tasks as any[]) || []).filter((t) => t.is_completed && !t.verified).length;
  // Children get the simplified menu (D9). Grown-ups (and adult non-admin members) keep the full one.
  const isChildMenu = !!current_user && !current_user.is_admin && current_user.member_type === 'child';
  const myPoints = Number(current_family?.members?.find((m: any) => m.id === current_user?.id)?.points ?? current_user?.points ?? 0);
  const childRowClass = 'w-full min-h-[64px] flex items-center gap-3 px-4 rounded-kid-card border-2 border-kid-border dark:border-kid-border-dark bg-kid-card dark:bg-kid-card-dark font-bold text-lg';
  const [showConfirm, setShowConfirm] = React.useState(false);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const location = useLocation();
  const { current_language } = useLanguage();
  const drawerCloseBtnRef = useRef<HTMLButtonElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const prevDrawerOpen = useRef(false);

  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  const isActive = (path: string) => location.pathname === path;

  useEffect(() => {
    closeDrawer();
  }, [location.pathname, closeDrawer]);

  useEffect(() => {
    if (!drawerOpen) return;
    const t = window.setTimeout(() => drawerCloseBtnRef.current?.focus(), 50);
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeDrawer();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      window.clearTimeout(t);
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKey);
    };
  }, [drawerOpen, closeDrawer]);

  useEffect(() => {
    if (prevDrawerOpen.current && !drawerOpen) {
      menuButtonRef.current?.focus();
    }
    prevDrawerOpen.current = drawerOpen;
  }, [drawerOpen]);

  const openDrawer = () => setDrawerOpen(true);

  const drawerLinkClass = (active: boolean) =>
    `flex items-center gap-3 px-4 min-h-[56px] text-lg font-bold rounded-kid-card border-2 ${
      active
        ? 'bg-kid-green-bg dark:bg-green-900/40 border-kid-green text-kid-green-shadow dark:text-green-200'
        : 'border-transparent text-kid-text dark:text-kid-text-dark hover:bg-kid-chip dark:hover:bg-kid-chip-dark'
    }`;

  return (
    <>
      <header
        className="w-full min-w-0 shrink-0 bg-surface-card dark:bg-gray-900 shadow-md border-b border-border-default dark:border-gray-700 transition-colors duration-200 z-40 relative pt-[env(safe-area-inset-top,0px)]"
        data-lang={current_language}
      >
        {/* Mobile top bar */}
        <div className="w-full max-w-[1180px] mx-auto px-3 sm:px-4 md:px-8 flex items-center justify-between gap-2 min-h-[3.5rem] md:min-h-[4rem] py-1">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <button
              ref={menuButtonRef}
              type="button"
              className="flex items-center justify-center min-w-[44px] min-h-[44px] rounded-lg text-app-text dark:text-gray-100 hover:bg-surface-muted dark:hover:bg-gray-800 -ml-1"
              aria-expanded={drawerOpen}
              aria-controls="mobile-nav-drawer"
              aria-label={drawerOpen ? i18n.t('header.menu_close') : i18n.t('header.menu_open')}
              onClick={() => setDrawerOpen((o) => !o)}
            >
              <HamburgerIcon open={drawerOpen} />
            </button>
            <Link
              to={HOME_PATH}
              className="flex items-center gap-2 min-w-0"
              aria-label={i18n.t('header.app_title')}
            >
              <img
                src="/AntNest_logo.png"
                alt=""
                className="w-10 h-10 sm:w-11 sm:h-11 object-contain rounded-md shrink-0"
              />
              <span className="text-lg font-bold text-app-text dark:text-gray-100 truncate">
                {i18n.t('header.app_title')}
              </span>
            </Link>
          </div>
          {current_user && (
            <Link
              to="/profile"
              className="flex items-center justify-center min-w-[44px] min-h-[44px] rounded-full border border-border-default dark:border-gray-600 overflow-hidden shrink-0"
              title={i18n.t('header.profile')}
            >
              <img
                src={resolveAssetUrl(avatarOrDefault((current_user as any).avatar_url))}
                alt=""
                className="w-9 h-9 object-cover"
              />
            </Link>
          )}
        </div>

      </header>

      {/* Mobile drawer + backdrop */}
      {current_user && (
        <div>
          <div
            className={`fixed inset-0 z-[45] bg-black/50 transition-opacity duration-200 ${
              drawerOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
            aria-hidden={!drawerOpen}
            onClick={closeDrawer}
          />
          <div
            id="mobile-nav-drawer"
            role="dialog"
            aria-modal="true"
            aria-label={i18n.t('header.nav_menu')}
            aria-hidden={!drawerOpen}
            className={`fixed top-0 left-0 z-[50] h-[100dvh] w-[min(20rem,88vw)] max-w-[100vw] bg-kid-page dark:bg-kid-bg-dark shadow-xl border-r border-border-default dark:border-gray-700 flex flex-col pt-[env(safe-area-inset-top,0px)] transition-transform duration-200 ease-out ${
              drawerOpen ? 'translate-x-0' : '-translate-x-full pointer-events-none'
            }`}
          >
            <div className="flex items-center justify-between px-3 py-3 border-b border-border-default dark:border-gray-700 shrink-0">
              <span className="text-lg font-semibold text-app-text dark:text-gray-100 pl-1">
                {i18n.t('header.nav_menu')}
              </span>
              <button
                ref={drawerCloseBtnRef}
                type="button"
                className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg hover:bg-surface-muted dark:hover:bg-gray-800 text-app-text dark:text-gray-100"
                aria-label={i18n.t('header.menu_close')}
                onClick={closeDrawer}
              >
                <HamburgerIcon open />
              </button>
            </div>
            {isChildMenu ? (
              /* A child's menu (kid-first redesign D9, mock-up phone-11): no Settings or Log out.
                 Both buttons end the child's turn and show "Who are you?" — a grown-up then picks
                 their own name and PIN. (Switch user would restore a parked parent session.) */
              <div className="flex-1 flex flex-col px-4 pb-[max(1rem,env(safe-area-inset-bottom,0px))] text-kid-text dark:text-kid-text-dark bg-kid-page dark:bg-kid-bg-dark">
                <div className="flex flex-col items-center text-center pt-4 pb-6">
                  <span className="w-24 h-24 rounded-full border-4 border-kid-green overflow-hidden bg-kid-peach flex items-center justify-center font-kid-heading font-extrabold text-5xl">
                    {(current_user as any).avatar_url
                      ? <img src={resolveAssetUrl((current_user as any).avatar_url)} alt="" className="w-full h-full object-cover" />
                      : current_user.name.trim().charAt(0).toLocaleUpperCase()}
                  </span>
                  <p className="font-kid-heading font-extrabold text-3xl mt-2">{current_user.name}</p>
                  <span className="mt-1 inline-flex items-center gap-1.5 rounded-full bg-kid-peach dark:bg-amber-900/50 px-3 min-h-[36px] font-bold">
                    <KidIcon name="star" size={18} />
                    {i18n.t(Number(myPoints) === 1 ? 'common.stars_one' : 'common.stars_other').replace('{{n}}', String(myPoints))}
                  </span>
                </div>
                <Link to="/profile" onClick={closeDrawer} className={childRowClass}>
                  <span className="text-2xl" aria-hidden>🙂</span>
                  <span className="flex-1">{i18n.t('header.my_page')}</span>
                  <span aria-hidden>›</span>
                </Link>
                <button type="button" onClick={() => { closeDrawer(); endMemberTurn(); }} className={childRowClass + ' mt-3'}>
                  <span className="text-2xl" aria-hidden>👥</span>
                  <span className="flex-1 text-left">{i18n.t('header.someone_else')}</span>
                  <span aria-hidden>›</span>
                </button>
                <button
                  type="button"
                  onClick={() => { closeDrawer(); endMemberTurn(); }}
                  className="mt-auto w-full min-h-[64px] flex items-center gap-3 px-4 py-3 rounded-kid-card border-2 border-kid-border-strong dark:border-kid-border-strong-dark bg-kid-chip dark:bg-kid-chip-dark text-left"
                >
                  <KidIcon name="lock" size={28} />
                  <span className="flex-1">
                    <span className="block font-bold text-lg leading-tight">{i18n.t('header.for_grownups')}</span>
                    <span className="block text-sm">{i18n.t('header.for_grownups_hint')}</span>
                  </span>
                  <span aria-hidden>›</span>
                </button>
              </div>
            ) : (
              <>
              <div className="px-3 py-4 border-b-2 border-kid-border dark:border-kid-border-dark shrink-0">
                <Link
                  to="/profile"
                  className="flex items-center gap-3 min-h-[56px] text-kid-text dark:text-kid-text-dark"
                  onClick={closeDrawer}
                >
                  <img
                    src={resolveAssetUrl(avatarOrDefault((current_user as any).avatar_url))}
                    alt=""
                    className="w-14 h-14 rounded-full object-cover border-2 border-kid-border dark:border-kid-border-dark"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="font-kid-heading font-extrabold text-xl truncate">{current_user.name}</div>
                    <div className="inline-flex items-center gap-1 text-base font-bold">
                      <KidIcon name="star" size={16} /> {i18n.t(Number(myPoints) === 1 ? 'common.stars_one' : 'common.stars_other').replace('{{n}}', String(myPoints))}
                    </div>
                  </div>
                  <span aria-hidden>›</span>
                </Link>
              </div>
              <nav className="flex-1 overflow-y-auto py-2 px-2" aria-label={i18n.t('header.nav_menu')}>
                <Link
                  to={HOME_PATH}
                  className={drawerLinkClass(isActive(HOME_PATH))}
                  onClick={closeDrawer}
                >
                  <KidIcon name={NAV_TAB_ICONS.home} size={28} className="shrink-0" />
                  {i18n.t('header.home')}
                </Link>
                {current_user?.is_admin && (
                  <Link to="/approve" className={drawerLinkClass(isActive('/approve'))} onClick={closeDrawer}>
                    <span className="w-7 text-center text-2xl shrink-0" aria-hidden>✓</span>
                    <span className="flex-1">{i18n.t('approve.title')}</span>
                    {pendingApprovals > 0 && (
                      <span className="min-w-[28px] h-7 px-2 rounded-full bg-kid-orange text-white text-base flex items-center justify-center">{pendingApprovals}</span>
                    )}
                  </Link>
                )}
                <Link
                  to="/tasks"
                  className={drawerLinkClass(isActive('/tasks'))}
                  onClick={closeDrawer}
                >
                  <KidIcon name={NAV_TAB_ICONS.tasks} size={28} className="shrink-0" />
                  {i18n.t('header.tasks')}
                </Link>
                <Link
                  to="/calendar"
                  className={drawerLinkClass(isActive('/calendar'))}
                  onClick={closeDrawer}
                >
                  <KidIcon name={NAV_TAB_ICONS.calendar} size={28} className="shrink-0" />
                  {i18n.t('header.calendar')}
                </Link>
                <Link
                  to="/rewards"
                  className={drawerLinkClass(isActive('/rewards'))}
                  onClick={closeDrawer}
                >
                  <KidIcon name={NAV_TAB_ICONS.rewards} size={28} className="shrink-0" />
                  {i18n.t('header.rewards')}
                </Link>
                <Link
                  to="/stats"
                  className={drawerLinkClass(isActive('/stats'))}
                  onClick={closeDrawer}
                >
                  <KidIcon name={NAV_TAB_ICONS.stats} size={28} className="shrink-0" />
                  {i18n.t('header.stats')}
                </Link>
                <Link
                  to="/settings"
                  className={drawerLinkClass(isActive('/settings'))}
                  onClick={closeDrawer}
                >
                  <KidIcon name={NAV_UI_ICONS.settings} size={28} className="shrink-0" />
                  {i18n.t('header.settings')}
                </Link>
                {current_user?.is_admin && (
                  <Link
                    to="/admin"
                    className={drawerLinkClass(isActive('/admin'))}
                    onClick={closeDrawer}
                  >
                    <KidIcon name={NAV_UI_ICONS.admin} size={28} className="shrink-0" />
                    {i18n.t('header.admin')}
                  </Link>
                )}
                {isGlobalCatalogAdmin(current_user) && (
                  <Link
                    to="/global-admin/tools"
                    className={drawerLinkClass(location.pathname.startsWith('/global-admin'))}
                    onClick={closeDrawer}
                  >
                    <span className="text-xl" aria-hidden>
                      ⭐
                    </span>
                    {i18n.t('header.master_admin')}
                  </Link>
                )}
              </nav>
              <div className="p-2 border-t-2 border-kid-border dark:border-kid-border-dark space-y-1 pb-[max(0.5rem,env(safe-area-inset-bottom,0px))] shrink-0">
                <button type="button" onClick={() => { closeDrawer(); switchUser(); }} className={drawerLinkClass(false) + ' w-full text-left'}>
                  <span className="w-7 text-center text-xl shrink-0" aria-hidden>👥</span>
                  <span className="flex-1">{i18n.t('header.switch_user')}</span>
                </button>
                <button type="button" onClick={() => { closeDrawer(); setShowConfirm(true); }} className={drawerLinkClass(false) + ' w-full text-left'}>
                  <span className="w-7 text-center text-xl shrink-0" aria-hidden>⇥</span>
                  <span className="flex-1">{i18n.t('settings.log_out_device')}</span>
                </button>
              </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Mobile bottom tab bar */}
      {current_user && (
        <nav
          className={`fixed bottom-0 left-0 right-0 z-[35] bg-kid-card dark:bg-gray-900 border-t border-border-default dark:border-gray-700 pb-[max(0.25rem,env(safe-area-inset-bottom,0px))] shadow-[0_-4px_12px_rgba(0,0,0,0.06)] dark:shadow-[0_-4px_12px_rgba(0,0,0,0.25)]`}
          aria-label={i18n.t('header.tab_bar_label')}
        >
          <div className="flex items-stretch justify-around gap-1 md:gap-3 max-w-lg md:max-w-2xl mx-auto px-1.5 md:px-4 py-1.5 md:py-2">
            {[
              { to: HOME_PATH, icon: NAV_TAB_ICONS.home, label: i18n.t('header.home') },
              { to: '/tasks', icon: NAV_TAB_ICONS.tasks, label: i18n.t('header.tasks') },
              { to: '/calendar', icon: NAV_TAB_ICONS.calendar, label: i18n.t('header.calendar') },
              { to: '/rewards', icon: NAV_TAB_ICONS.rewards, label: i18n.t('header.rewards') },
              { to: '/stats', icon: NAV_TAB_ICONS.stats, label: i18n.t('header.stats') },
            ].map((tab) => (
              <NavLink
                key={tab.to}
                to={tab.to}
                className={({ isActive }) =>
                  `flex flex-col items-center justify-start flex-1 min-w-0 min-h-[60px] md:min-h-[72px] gap-1 px-0.5 pt-1.5 md:pt-2.5 pb-1 rounded-2xl transition-colors ${
                    isActive
                      ? 'bg-kid-green-bg dark:bg-green-900/40 text-kid-green-shadow dark:text-green-300'
                      : 'text-text-secondary dark:text-gray-400 hover:text-app-text dark:hover:text-gray-200'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <KidIcon name={tab.icon} size={26} className="md:w-8 md:h-8" />
                    <span lang={current_language} className="w-full text-[13px] md:text-[15px] font-bold text-center leading-[1.1] hyphens-auto break-words">
                      {tab.label}
                    </span>
                  </>
                )}
              </NavLink>
            ))}
          </div>
        </nav>
      )}

      <ConfirmDialog
        open={showConfirm}
        title={i18n.t('settings.logout_confirm_title')}
        message={i18n.t('settings.logout_confirm_text').replace('{{family}}', current_family?.name || '')}
        confirmLabel={i18n.t('settings.logout_confirm_yes')}
        cancelLabel={i18n.t('settings.cancel')}
        onCancel={() => setShowConfirm(false)}
        onConfirm={async () => { await deviceLogout(); setShowConfirm(false); }}
      />
    </>
  );
};

export default Header;
